import React, { useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, type FormikProps } from "formik";
import * as Yup from "yup";

import {
  getGetDealGeneralPartnerViewV3QueryKey,
  useCancelDeal,
} from "@vega-api/generated/deal/deal";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice, NoticeType } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

type CancelDealFormValues = {
  confirmation: string;
};

export type CancelDealModalProps = {
  isOpen: boolean;
  onClose: () => void;
  dealId: string;
};

const validationSchema = Yup.object({
  confirmation: Yup.string().test(
    "is-delete",
    m.coInvestments_cancelDeal_modal_confirmationRequired(),
    (value) => value === m.coInvestments_cancelDeal_modal_validationWord(),
  ),
});

export const CancelDealModal = ({
  isOpen,
  onClose,
  dealId,
}: CancelDealModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const { mutate: cancelDeal, isPending: isSubmitting } = useCancelDeal({
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.status_success(),
          description: m.coInvestments_cancelDeal_toast_success(),
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealGeneralPartnerViewV3QueryKey(dealId),
        });
        onClose();
      },
      onError: (error) => {
        showErrorToast(error, {
          defaultMessage: m.coInvestments_cancelDeal_toast_error(),
        });
      },
    },
  });

  const formRef = useRef<FormikProps<CancelDealFormValues>>(null);

  const initialValues: CancelDealFormValues = {
    confirmation: "",
  };

  const handleSubmit = async () => {
    cancelDeal({
      id: dealId,
    });
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainActionLabel: m.btn_confirm(),
        mainAction: () => formRef.current?.submitForm(),
        mainActionText: m.btn_confirm(),
        disabled: isSubmitting,
        secondaryAction: onClose,
        secondaryActionLabel: m.btn_cancel(),
        secondaryActionText: m.btn_cancel(),
        disabledSecondary: isSubmitting,
      }}
      dataTestId="cancel-deal-modal"
      fluidHeight
      open={isOpen}
      setOpen={onClose}
      title={m.coInvestments_cancelDeal_modal_title()}
    >
      <Formik
        initialValues={initialValues}
        innerRef={formRef}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form>
          <div className="flex flex-col gap-8 w-dvw max-w-md">
            <Notice
              content={m.coInvestments_cancelDeal_modal_warning_notice()}
              type={NoticeType.ERROR}
            />

            <InputField
              label={m.coInvestments_cancelDeal_modal_confirmationLabel()}
              name="confirmation"
            />
          </div>
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
